<template>
	<v-container class="fill-wh community-search">
		<div class="community-search-form">
			<v-autocomplete
				v-model="form.community"
				:search-input.sync="form.communityName"
				class="community-auto mb-0 mb-sm-9"
				label="Select a community"
				:items="communities"
				item-text="name"
				return-object
				solo
				clearable
				hide-details
			>
				<template #prepend-inner>
					<group-icon class="mr-2" :group="selectedCommunityIcon" />
				</template>

				<template #append-outer>
					<v-btn
						class="d-sm-block d-none"
						text
						:disabled="!form.community"
						@click="navCommunity"
						>Select
						<v-icon small class="ml-2">fas fa-arrow-right</v-icon></v-btn
					>
				</template>

				<template #item="{ item }">
					<group-icon class="mr-2" :group="item" />
					<div class="highlight-text">
						<span
							v-for="(text, i) in breakSelectedText(
								item.name,
								form.communityName
							)"
							:key="i"
							v-text="text"
						/>
					</div>
				</template>
			</v-autocomplete>

			<v-btn
				class="d-sm-none"
				text
				:disabled="!form.community"
				@click="navCommunity"
				>Select <v-icon small class="ml-2">fas fa-arrow-right</v-icon></v-btn
			>

			<v-autocomplete
				v-model="form.item"
				:search-input.sync="form.search"
				:filter="returnHeadersAndFilter"
				label="Or search in community"
				:items="communityItems"
				item-text="name"
				return-object
				solo
				hide-details
				@change="navSearch"
			>
				<template #prepend-inner>
					<v-icon
						v-if="form.item"
						class="mr-2"
						v-text="getItemIcon(form.item)"
					/>
				</template>

				<template #item="{ item }">
					<v-icon class="mr-2" v-text="getItemIcon(item)" />
					<div class="highlight-text">
						<span
							v-for="(text, i) in breakSelectedText(item.name, form.search)"
							:key="i"
							v-text="text"
						/>
					</div>
				</template>
			</v-autocomplete>
		</div>
	</v-container>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import { createUser } from "@/store/model/user";
import api from "@/api/cloud";

export default {
	props: {
		showSearch: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			strings: {
				title: "Community Search",
			},
			form: {
				communityName: null,
				community: null,
				search: null,
				item: null,
			},
			communityUsers: [],
		};
	},

	computed: {
		...mapGetters({
			_communities: "communities/items",
			selectedCommunity: "session/community",
			communitySystems: "rooms/items",
		}),

		selectedCommunityIcon() {
			return this.form.community || {};
		},

		communities() {
			return _.chain(this._communities)
				.filter((community) => this.$can("read", community))
				.sortBy("name")
				.value();
		},

		installedSystems() {
			return _.filter(this.communitySystems, { installed: true });
		},

		communityItems() {
			return [
				{ header: "Systems" },
				..._.sortBy(this.installedSystems, "name"),
				{ header: "Users" },
				..._.sortBy(this.communityUsers, "name"),
			];
		},
	},

	watch: {
		"form.community"(community) {
			this.loadCommunityItems(community);
		},
	},

	created() {
		if (this.selectedCommunity) {
			this.form.community = this.selectedCommunity;
		}
	},

	methods: {
		...mapActions({
			getRooms: "rooms/load",
		}),

		async getUsers(communityId) {
			this.communityUsers = _.map(
				await api.getAssociationUsers(communityId),
				createUser
			).map((user) => ({ ...user, name: String(user.displayName) }));
		},

		async loadCommunityItems(community) {
			if (!community) {
				this.communityUsers = [];
				this.$store.commit("rooms/resetState");
				return;
			}
			await Promise.all([
				this.getRooms(community),
				this.getUsers(community.id),
			]);
		},

		getItemIcon(item) {
			return this.isItemSystem(item) ? "fas fa-door-open" : "fas fa-user";
		},

		isItemSystem(item) {
			return "mac" in item;
		},

		// Performs the highligh based on search
		breakSelectedText(text, search) {
			if (!search) return [text];

			const split = (str) => {
				const idx = str.toLowerCase().indexOf(search.toLowerCase());
				if (!~idx) return [str];

				return [
					str.substr(0, idx),
					str.substr(idx, search.length),
					...split(str.substr(idx + search.length)),
				];
			};

			return split(text);
		},

		returnHeadersAndFilter(item, queryText, itemText) {
			if (item.header) {
				return true;
			}
			return (
				itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
			);
		},

		navCommunity() {
			const community = this.form.community;
			const next = this.$route.meta.next
				? {
						...this.$route.meta.next,
						params: { communityId: community.id },
				  }
				: {
						path: `${community.id}`,
						append: true,
				  };
			return this.$router.push(next);
		},

		navSearch(item) {
			if (!item) return;

			const communityId = this.form.community.id;
			let next = {
				name: this.isItemSystem(item) ? "Live System" : "Live User",
				params: {
					communityId,
					systemId: item.id,
					userId: item.guid,
				},
			};

			return this.$router.push(next);
		},
	},
};
</script>

<style lang="scss" scoped>
.community-search {
	display: flex;
	flex-flow: column;
	justify-content: center;
	align-items: center;

	.community-search-form {
		width: 600px;
		min-width: 75%;
		max-width: 100%;
		text-align: end;
	}
}

.community-auto {
	align-items: center;

	::v-deep.v-input__append-outer {
		margin: 0;
	}
}

.highlight-text span:nth-child(even) {
	color: var(--v-ocean-darken4);
	background: var(--v-primary-lighten5);
}
</style>
